import type { AttendanceCorrectionRequestDetails } from '../types/attendanceCorrection';
import { displayCorrectionBreaks } from './attendanceCorrectionDraft';

export type AttendanceCorrectionBreakHighlight = {
  start: boolean;
  end: boolean;
};

export type AttendanceCorrectionSubmittedHighlight = {
  checkIn: boolean;
  checkOut: boolean;
  breaks: AttendanceCorrectionBreakHighlight[];
};

function text(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  return null;
}

function submittedFlag(value: unknown): boolean | null {
  if (value === true || value === 1 || value === '1' || value === 'true') {
    return true;
  }
  if (value === false || value === 0 || value === '0' || value === 'false') {
    return false;
  }
  return null;
}

function hasKey(details: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(details, key);
}

function objectRow(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function fieldSubmitted(
  flagValue: unknown,
  existingValue: unknown,
  requestedValue: unknown,
  hasMetadata: boolean,
): boolean {
  const existing = text(existingValue);
  const requested = text(requestedValue);
  if (existing && requested && existing !== requested) return true;
  // The server flag decides when present: a request without a check-out
  // keeps the recorded one, so an empty requested value is not a change.
  const parsed = submittedFlag(flagValue);
  if (parsed !== null) return parsed;
  if (hasMetadata && existing && !requested) return true;
  if (!hasMetadata) return false;
  return !existing && Boolean(requested);
}

export function attendanceCorrectionDisplayRecord(
  details: AttendanceCorrectionRequestDetails,
): Record<string, unknown> {
  const record: Record<string, unknown> = {
    attendanceDate: details.attendanceDate,
    checkInTime: details.checkInTime,
    checkOutTime: details.checkOutTime,
    breakStartTime: details.breakStartTime,
    breakEndTime: details.breakEndTime,
    breaks: details.breaks,
    removedBreaks: details.removedBreaks ?? [],
    reason: details.reason,
    submittedAt: details.submittedAt,
    rejectReason: details.rejectReason,
    cancellationReason: details.cancellationReason,
    checkInSource: details.checkInSource,
    checkInOriginalSource: details.checkInOriginalSource,
    checkInIsManuallyEdited: details.checkInIsManuallyEdited,
    checkInKioskId: details.checkInKioskId,
    checkInFaceVerified: details.checkInFaceVerified,
    checkInFaceVerificationLogId: details.checkInFaceVerificationLogId,
    checkInDeviceIdentifier: details.checkInDeviceIdentifier,
    checkOutSource: details.checkOutSource,
    checkOutOriginalSource: details.checkOutOriginalSource,
    checkOutIsManuallyEdited: details.checkOutIsManuallyEdited,
    checkOutKioskId: details.checkOutKioskId,
    checkOutFaceVerified: details.checkOutFaceVerified,
    checkOutFaceVerificationLogId: details.checkOutFaceVerificationLogId,
    checkOutDeviceIdentifier: details.checkOutDeviceIdentifier,
    editedByName: details.editedByName,
    requesterName: details.requesterName,
    editedAt: details.submittedAt,
  };
  if (details.checkInSubmitted != null) {
    record.checkInSubmitted = details.checkInSubmitted;
  }
  if (details.checkOutSubmitted != null) {
    record.checkOutSubmitted = details.checkOutSubmitted;
  }
  if (details.breakStartSubmitted != null) {
    record.breakStartSubmitted = details.breakStartSubmitted;
  }
  if (details.breakEndSubmitted != null) {
    record.breakEndSubmitted = details.breakEndSubmitted;
  }
  if (details.checkInSubmitted != null || details.existingCheckInTime) {
    record.existingCheckInTime = details.existingCheckInTime;
  }
  if (details.checkOutSubmitted != null || details.existingCheckOutTime) {
    record.existingCheckOutTime = details.existingCheckOutTime;
  }
  if (details.breakStartSubmitted != null || details.existingBreakStartTime) {
    record.existingBreakStartTime = details.existingBreakStartTime;
  }
  if (details.breakEndSubmitted != null || details.existingBreakEndTime) {
    record.existingBreakEndTime = details.existingBreakEndTime;
  }
  return record;
}

export function attendanceCorrectionSubmittedHighlight(
  requestDetails: Record<string, unknown> | null | undefined,
): AttendanceCorrectionSubmittedHighlight {
  const details = requestDetails ?? {};
  const parentCheckInMeta =
    submittedFlag(details.checkInSubmitted) !== null ||
    hasKey(details, 'existingCheckInTime') ||
    hasKey(details, 'existingCheckInAt');
  const parentCheckOutMeta =
    submittedFlag(details.checkOutSubmitted) !== null ||
    hasKey(details, 'existingCheckOutTime') ||
    hasKey(details, 'existingCheckOutAt');
  const parentBreakStartMeta =
    submittedFlag(details.breakStartSubmitted) !== null ||
    hasKey(details, 'existingBreakStartTime') ||
    hasKey(details, 'existingBreakStartAt');
  const parentBreakEndMeta =
    submittedFlag(details.breakEndSubmitted) !== null ||
    hasKey(details, 'existingBreakEndTime') ||
    hasKey(details, 'existingBreakEndAt');

  const displayed = displayCorrectionBreaks({
    breaks: details.breaks,
    breakStartTime: text(
      details.breakStartTime ?? details.requestedBreakStartTime,
    ),
    breakEndTime: text(details.breakEndTime ?? details.requestedBreakEndTime),
  });
  const rawBreaks = Array.isArray(details.breaks) ? details.breaks : [];

  const breaks = displayed.map((_, index) => {
    const row = objectRow(rawBreaks[index]);
    const startFlag = row
      ? row.startSubmitted ?? row.start_submitted
      : undefined;
    const endFlag = row ? row.endSubmitted ?? row.end_submitted : undefined;
    const existingStart = row
      ? row.existingStartTime ?? row.existing_start_time
      : undefined;
    const existingEnd = row
      ? row.existingEndTime ?? row.existing_end_time
      : undefined;
    const itemStartMeta =
      submittedFlag(startFlag) !== null || text(existingStart) !== null;
    const itemEndMeta =
      submittedFlag(endFlag) !== null || text(existingEnd) !== null;

    const startMeta =
      itemStartMeta ||
      (index === 0 && parentBreakStartMeta) ||
      parentCheckInMeta ||
      parentCheckOutMeta;
    const endMeta =
      itemEndMeta ||
      (index === 0 && parentBreakEndMeta) ||
      parentCheckInMeta ||
      parentCheckOutMeta;

    const requestedStart = row
      ? row.startTime ?? row.breakStartTime
      : index === 0
        ? details.breakStartTime
        : undefined;
    const requestedEnd = row
      ? row.endTime ?? row.breakEndTime
      : index === 0
        ? details.breakEndTime
        : undefined;

    return {
      start: fieldSubmitted(
        startFlag ??
          (index === 0 ? details.breakStartSubmitted : undefined),
        existingStart ??
          (index === 0 ? details.existingBreakStartTime : undefined),
        requestedStart,
        startMeta,
      ),
      end: fieldSubmitted(
        endFlag ?? (index === 0 ? details.breakEndSubmitted : undefined),
        existingEnd ??
          (index === 0 ? details.existingBreakEndTime : undefined),
        requestedEnd,
        endMeta,
      ),
    };
  });

  return {
    checkIn: fieldSubmitted(
      details.checkInSubmitted,
      details.existingCheckInTime ?? details.existingCheckInAt,
      details.checkInTime ?? details.requestedCheckInTime,
      parentCheckInMeta,
    ),
    checkOut: fieldSubmitted(
      details.checkOutSubmitted,
      details.existingCheckOutTime ?? details.existingCheckOutAt,
      details.checkOutTime ?? details.requestedCheckOutTime,
      parentCheckOutMeta,
    ),
    breaks,
  };
}
